import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import {
  adminService,
  type TargetingCondition,
  type TargetingRule,
} from '../../services/adminService'
import { EmptyState, LoadingState } from '../../components/ui/States'
import { apiError } from '../errors'
import { useToast } from '../useToast'
import { confirmDelete } from '../confirm'

// Friendly labels for the built-in attendee fields. Any other key is a custom
// tag (attendee_attributes) an admin set on attendees, shown as-is.
const BUILTIN_KEY_LABELS: Record<string, string> = {
  track: 'Track',
  attendee_type: 'Type',
  company: 'Company',
}
const isBuiltinKey = (k: string) => k in BUILTIN_KEY_LABELS
const keyLabel = (k: string) => BUILTIN_KEY_LABELS[k] ?? `Tag: ${k}`

function summarize(rule: TargetingRule): string {
  if (rule.includeAll) return 'All attendees'
  if (rule.conditions.length === 0) return 'No one (empty rule)'
  const parts = rule.conditions.map((c) => {
    const v = Array.isArray(c.value) ? c.value.join(', ') : c.value
    const opWord = c.op === 'in' ? 'in' : c.op === 'neq' ? '≠' : '='
    return `${keyLabel(c.key)} ${opWord} ${v}`
  })
  return parts.join(rule.matchType === 'any' ? '  OR  ' : '  AND  ')
}

export default function TargetingManager({ eventId }: { eventId: string }) {
  const { notify } = useToast()
  const [rules, setRules] = useState<TargetingRule[]>([])
  const [keys, setKeys] = useState<{ key: string; values: string[] }[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<TargetingRule | null>(null)
  const [open, setOpen] = useState(false)

  const load = () =>
    Promise.all([
      adminService.listRules(eventId),
      adminService.targetingKeys(eventId),
    ])
      .then(([r, k]) => {
        setRules(r)
        setKeys(k.keys)
      })
      .catch((e) => notify(apiError(e), 'error'))
      .finally(() => setLoading(false))

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId])

  const remove = async (r: TargetingRule) => {
    if (
      !(await confirmDelete(
        `Delete rule "${r.name}"? Items using it will become visible to everyone.`,
      ))
    )
      return
    try {
      await adminService.deleteRule(eventId, r.id)
      notify('Rule deleted')
      load()
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }

  if (loading) return <LoadingState />

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Rules control who sees a session, content item, or form. Assign a rule
          when editing those items.
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddRoundedIcon />}
          onClick={() => {
            setEditing(null)
            setOpen(true)
          }}
        >
          New rule
        </Button>
      </Stack>

      {rules.length === 0 ? (
        <EmptyState
          title="No targeting rules"
          description="Create a rule (e.g. track is Executive Briefing) to personalize what attendees see."
        />
      ) : (
        <Stack spacing={1.5}>
          {rules.map((r) => (
            <Card
              key={r.id}
              variant="outlined"
              sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, borderRadius: 3 }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle1" fontWeight={700}>
                    {r.name}
                  </Typography>
                  {r.includeAll && (
                    <Chip label="All" size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
                  )}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {summarize(r)}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => {
                  setEditing(r)
                  setOpen(true)
                }}
              >
                <EditRoundedIcon />
              </IconButton>
              <IconButton size="small" color="error" onClick={() => remove(r)}>
                <DeleteRoundedIcon />
              </IconButton>
            </Card>
          ))}
        </Stack>
      )}

      {open && (
        <RuleDialog
          eventId={eventId}
          rule={editing}
          attributeKeys={keys}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false)
            load()
          }}
        />
      )}
    </Box>
  )
}

interface EditableCondition {
  key: string
  op: 'eq' | 'neq' | 'in'
  value: string // comma-separated for `in`
}

function RuleDialog({
  eventId,
  rule,
  attributeKeys,
  onClose,
  onSaved,
}: {
  eventId: string
  rule: TargetingRule | null
  attributeKeys: { key: string; values: string[] }[]
  onClose: () => void
  onSaved: () => void
}) {
  const { notify } = useToast()
  const [saving, setSaving] = useState(false)
  const [name, setName] = useState(rule?.name ?? '')
  const [matchType, setMatchType] = useState<'all' | 'any'>(rule?.matchType ?? 'all')
  const [includeAll, setIncludeAll] = useState(rule?.includeAll ?? false)
  const [conditions, setConditions] = useState<EditableCondition[]>(
    rule && rule.conditions.length
      ? rule.conditions.map((c) => ({
          key: c.key,
          op: c.op,
          value: Array.isArray(c.value) ? c.value.join(', ') : c.value,
        }))
      : [{ key: attributeKeys[0]?.key ?? 'track', op: 'in', value: '' }],
  )

  const updateCond = (i: number, patch: Partial<EditableCondition>) =>
    setConditions((cs) => cs.map((c, idx) => (idx === i ? { ...c, ...patch } : c)))
  const addCond = () =>
    setConditions((cs) => [...cs, { key: attributeKeys[0]?.key ?? 'track', op: 'in', value: '' }])
  const removeCond = (i: number) => setConditions((cs) => cs.filter((_, idx) => idx !== i))

  const valuesFor = (key: string) =>
    attributeKeys.find((k) => k.key === key)?.values ?? []

  const save = async () => {
    if (!name.trim()) {
      notify('Rule name is required', 'error')
      return
    }
    const cleaned: TargetingCondition[] = includeAll
      ? []
      : conditions
          .filter((c) => c.key && c.value.trim())
          .map((c) => ({
            key: c.key,
            op: c.op,
            value:
              c.op === 'in'
                ? c.value.split(',').map((v) => v.trim()).filter(Boolean)
                : c.value.trim(),
          }))
    if (!includeAll && cleaned.length === 0) {
      notify('Add at least one condition, or enable "All attendees"', 'error')
      return
    }
    setSaving(true)
    try {
      const payload = { name: name.trim(), matchType, includeAll, conditions: cleaned }
      if (rule) await adminService.updateRule(eventId, rule.id, payload)
      else await adminService.createRule(eventId, payload)
      notify(rule ? 'Rule updated' : 'Rule created')
      onSaved()
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{rule ? 'Edit rule' : 'New targeting rule'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Rule name"
            required
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <FormControlLabel
            control={
              <Switch checked={includeAll} onChange={(e) => setIncludeAll(e.target.checked)} />
            }
            label="Visible to all attendees"
          />

          {!includeAll && (
            <>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  Match
                </Typography>
                <Select
                  size="small"
                  value={matchType}
                  onChange={(e) => setMatchType(e.target.value as 'all' | 'any')}
                >
                  <MenuItem value="all">ALL conditions</MenuItem>
                  <MenuItem value="any">ANY condition</MenuItem>
                </Select>
              </Stack>

              {conditions.map((c, i) => {
                const known = valuesFor(c.key)
                return (
                  <Card key={i} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Select
                        size="small"
                        value={c.key}
                        onChange={(e) => updateCond(i, { key: e.target.value })}
                        sx={{ minWidth: 150 }}
                        renderValue={(v) => keyLabel(v as string)}
                      >
                        {attributeKeys
                          .filter((k) => isBuiltinKey(k.key))
                          .map((k) => (
                            <MenuItem key={k.key} value={k.key}>
                              {keyLabel(k.key)}
                            </MenuItem>
                          ))}
                        {attributeKeys.some((k) => !isBuiltinKey(k.key)) && (
                          <ListSubheader>Tags</ListSubheader>
                        )}
                        {attributeKeys
                          .filter((k) => !isBuiltinKey(k.key))
                          .map((k) => (
                            <MenuItem key={k.key} value={k.key}>
                              {k.key}
                            </MenuItem>
                          ))}
                        {!attributeKeys.some((k) => k.key === c.key) && (
                          <MenuItem value={c.key}>{keyLabel(c.key)}</MenuItem>
                        )}
                      </Select>
                      <Select
                        size="small"
                        value={c.op}
                        onChange={(e) =>
                          updateCond(i, { op: e.target.value as EditableCondition['op'] })
                        }
                        sx={{ minWidth: 90 }}
                      >
                        <MenuItem value="in">is any of</MenuItem>
                        <MenuItem value="eq">is</MenuItem>
                        <MenuItem value="neq">is not</MenuItem>
                      </Select>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeCond(i)}
                        disabled={conditions.length === 1}
                      >
                        <DeleteRoundedIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    <TextField
                      fullWidth
                      size="small"
                      sx={{ mt: 1 }}
                      placeholder={
                        c.op === 'in' ? 'Comma-separated values' : 'Value'
                      }
                      helperText={
                        known.length ? `Known: ${known.join(', ')}` : undefined
                      }
                      value={c.value}
                      onChange={(e) => updateCond(i, { value: e.target.value })}
                    />
                  </Card>
                )
              })}
              <Button
                startIcon={<AddRoundedIcon />}
                onClick={addCond}
                sx={{ alignSelf: 'flex-start' }}
              >
                Add condition
              </Button>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
